"use client"

import { useRef } from "react"
import { motion, useInView } from "framer-motion"
import type React from "react"
import { Button } from "@/components/ui/button"
import { ArrowRight, BarChart2, ChevronRight } from "lucide-react"
import { useNavigate } from "react-router-dom"

interface CTASectionProps {
  onRequestAccess: () => void
}

export const CTASection = ({ onRequestAccess }: CTASectionProps) => {
  const navigate = useNavigate()
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, amount: 0.3 })

  const handlePatternStudyClick = (e: React.MouseEvent) => {
    e.preventDefault()
    navigate("/studies")
  }

  // Placeholder since no customer testimonials are available yet
  const placeholderMessage =
    "Be the first to share your success story. Early adopters enjoy exclusive benefits."

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24" ref={ref}>
        <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700">
          {/* Background elements */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-[128px] opacity-10"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-violet-500 rounded-full mix-blend-multiply filter blur-[128px] opacity-10"></div>

          <div className="relative p-8 md:p-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.5 }}
              className="max-w-3xl mx-auto text-center"
            >
              <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
                Join the Quantum Trading <br className="hidden sm:inline" />
                Revolution
              </h2>
              <p className="text-lg sm:text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                Early access spots are limited. Reserve your seat now to experience the future of algorithmic trading
                designed specifically for wealth advisors.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-lg h-14 px-8 rounded-md text-white w-full sm:w-auto shadow-lg hover:shadow-xl transition-all duration-300"
                  onClick={onRequestAccess}
                >
                  Request Early Access <ArrowRight className="ml-2 h-5 w-5 text-white" />
                </Button>

                <Button
                  variant="outline"
                  size="lg"
                  className="text-lg h-14 px-8 rounded-md border-gray-600 hover:bg-gray-700 bg-gray-900/80 text-white font-medium w-full sm:w-auto"
                  onClick={() => navigate("/docs")}
                >
                  Learn More <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </motion.div>

            {/* Placeholder since no testimonials are available */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mt-16 text-center text-gray-300 italic"
            >
              {placeholderMessage}
            </motion.div>
          </div>
        </div>
      </div>

      {/* Pattern Recognition Study Button */}
      <div className="fixed bottom-8 right-8 z-50">
        <Button
          size="lg"
          className="rounded-full w-16 h-16 bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 shadow-lg hover:shadow-xl transition-all duration-300 group"
          aria-label="Pattern Recognition Study"
          onClick={handlePatternStudyClick}
        >
          <div className="flex flex-col items-center">
            <BarChart2 className="h-6 w-6 text-white" />
            <span className="absolute -top-12 right-0 bg-black/80 text-white text-sm py-1 px-3 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
              Pattern Recognition Study
            </span>
          </div>
        </Button>
      </div>
    </>
  )
}
